import { useState, useRef, useCallback, useEffect } from "react";
import { Bell, Trash2, Settings, Volume2, VolumeX, CheckCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { isToday, isYesterday, isThisWeek } from "date-fns";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SectionHeader, BodyText } from "@/components/ui/typography";
import { NotificationItem } from "./NotificationItem";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export interface Notification {
  id: string;
  type: "new_feedback" | "sentiment_alert" | "milestone";
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  pageTitle?: string;
  pageId?: string;
}

interface NotificationsPanelProps {
  notifications: Notification[];
  onMarkAsRead: (id: string) => void;
  onMarkAllAsRead: () => void;
  onDismiss: (id: string) => void;
  onClearAll: () => void;
  soundEnabled: boolean;
  onToggleSound: () => void;
  children?: React.ReactNode;
}

export const NotificationsPanel = ({
  notifications,
  onMarkAsRead,
  onMarkAllAsRead,
  onDismiss,
  onClearAll,
  soundEnabled,
  onToggleSound,
  children,
}: NotificationsPanelProps) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState<"all" | "new_feedback" | "sentiment_alert" | "milestone">("all");
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const notificationRefs = useRef<(HTMLDivElement | null)[]>([]);
  const unreadCount = notifications.filter((n) => !n.read).length;
  
  // Filter notifications based on type and unread status
  let filteredNotifications = typeFilter === "all" 
    ? notifications 
    : notifications.filter(n => n.type === typeFilter);
  
  if (showUnreadOnly) {
    filteredNotifications = filteredNotifications.filter(n => !n.read);
  }

  // Group notifications by date
  const groupNotificationsByDate = (notifs: Notification[]) => {
    const groups: { [key: string]: Notification[] } = {
      Today: [],
      Yesterday: [],
      "This Week": [],
      Earlier: [],
    };

    notifs.forEach((notif) => {
      const date = new Date(notif.timestamp);
      if (isToday(date)) {
        groups.Today.push(notif);
      } else if (isYesterday(date)) {
        groups.Yesterday.push(notif);
      } else if (isThisWeek(date, { weekStartsOn: 1 })) {
        groups["This Week"].push(notif);
      } else {
        groups.Earlier.push(notif);
      }
    });

    // Filter out empty groups
    return Object.entries(groups).filter(([_, notifs]) => notifs.length > 0);
  };

  const groupedNotifications = groupNotificationsByDate(filteredNotifications);


  const handleNotificationClick = (notification: Notification) => {
    // Mark as read
    if (!notification.read) {
      onMarkAsRead(notification.id);
    }
    
    // Navigate based on notification type
    if (notification.type === "new_feedback" && notification.pageId) {
      navigate(`/dashboard/feedback?page=${notification.pageId}`);
      setOpen(false);
    } else if (notification.type === "sentiment_alert") {
      if (notification.pageId) {
        navigate(`/dashboard/feedback?page=${notification.pageId}&sentiment=negative`);
      } else {
        navigate(`/dashboard/feedback?sentiment=negative`);
      }
      setOpen(false);
    }
  };

  // Keyboard navigation
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!open) return;
    
    const flatNotifications = filteredNotifications;
    
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setFocusedIndex(prev => 
          prev < flatNotifications.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setFocusedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case "Enter":
        if (focusedIndex >= 0 && focusedIndex < flatNotifications.length) {
          handleNotificationClick(flatNotifications[focusedIndex]);
        }
        break;
      case "r":
        if (focusedIndex >= 0 && focusedIndex < flatNotifications.length) {
          const notif = flatNotifications[focusedIndex];
          if (!notif.read) onMarkAsRead(notif.id);
        }
        break;
      case "d":
        if (focusedIndex >= 0 && focusedIndex < flatNotifications.length) {
          onDismiss(flatNotifications[focusedIndex].id);
          setFocusedIndex(prev => Math.min(prev, flatNotifications.length - 2));
        }
        break;
      case "Escape":
        setOpen(false);
        break;
    }
  }, [open, filteredNotifications, focusedIndex, onMarkAsRead, onDismiss]);

  useEffect(() => {
    if (open) {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [open, handleKeyDown]);

  // Focus on item when index changes
  useEffect(() => {
    if (focusedIndex >= 0 && notificationRefs.current[focusedIndex]) {
      notificationRefs.current[focusedIndex]?.scrollIntoView({ block: "nearest" });
    }
  }, [focusedIndex]);

  // Reset focus when panel closes
  useEffect(() => {
    if (!open) setFocusedIndex(-1);
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {children || (
          <Button variant="ghost" size="sm" className="relative min-h-[44px] min-w-[44px] bg-accent hover:bg-accent/80 transition-all duration-200 rounded-lg">
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-[95vw] max-w-96 p-0 rounded-[20px]" align="end">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <SectionHeader>Notifications</SectionHeader>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onMarkAllAsRead}
                    className="min-h-[44px] min-w-[44px] p-2 hover:bg-accent"
                    aria-label="Mark all as read"
                  >
                    <CheckCheck className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Mark all as read</TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onToggleSound}
                  className="min-h-[44px] min-w-[44px] p-2 hover:bg-accent"
                  aria-label={soundEnabled ? "Mute notifications" : "Unmute notifications"}
                >
                  {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{soundEnabled ? "Mute sounds" : "Enable sounds"}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    navigate("/dashboard/settings?tab=notifications");
                    setOpen(false);
                  }}
                  className="min-h-[44px] min-w-[44px] p-2 hover:bg-accent"
                  aria-label="Notification settings"
                >
                  <Settings className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Settings</TooltipContent>
            </Tooltip>
            {notifications.length > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onClearAll}
                    className="min-h-[44px] min-w-[44px] p-2 hover:bg-accent"
                    aria-label="Clear all notifications"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Clear all</TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>

        {/* Type Filter Tabs */}
        <div className="px-4 pt-3 pb-2 border-b space-y-2">
          <Tabs value={typeFilter} onValueChange={(v) => setTypeFilter(v as typeof typeFilter)} className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="all" className="text-xs">
                All
              </TabsTrigger>
              <TabsTrigger value="new_feedback" className="text-xs">
                Feedback
              </TabsTrigger>
              <TabsTrigger value="sentiment_alert" className="text-xs">
                Alerts
              </TabsTrigger>
              <TabsTrigger value="milestone" className="text-xs">
                Milestones
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex items-center gap-2">
            <Checkbox 
              id="unread-only" 
              checked={showUnreadOnly} 
              onCheckedChange={(checked) => setShowUnreadOnly(checked === true)}
            />
            <Label htmlFor="unread-only" className="text-xs cursor-pointer">
              Show unread only
            </Label>
          </div>
        </div>

        <ScrollArea className="h-[400px]">
          {filteredNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <Bell className="h-12 w-12 mb-4 opacity-20 text-muted-foreground" />
              <BodyText variant="secondary">
                {showUnreadOnly 
                  ? "No unread notifications"
                  : typeFilter === "all"
                  ? "No notifications yet"
                  : typeFilter === "new_feedback"
                  ? "No feedback notifications"
                  : typeFilter === "sentiment_alert"
                  ? "No alert notifications"
                  : "No milestone notifications"}
              </BodyText>
              <BodyText variant="secondary" className="text-xs mt-2">
                {!showUnreadOnly && "New notifications will appear here when you receive feedback"}
              </BodyText>
            </div>
          ) : (
            <div>
              {groupedNotifications.map(([dateGroup, groupNotifications]) => (
                <div key={dateGroup}>
                  <div className="px-4 py-2 bg-muted/30 sticky top-0 z-10">
                    <BodyText className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {dateGroup}
                    </BodyText>
                  </div>
                  <div className="divide-y">
                    {groupNotifications.map((notification) => (
                      <NotificationItem
                        key={notification.id}
                        notification={notification}
                        onMarkAsRead={onMarkAsRead}
                        onDismiss={onDismiss}
                        onClick={handleNotificationClick}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};
